import './ChatWindow.css';
import { api } from '../api/robyBackend';
import { marked } from 'marked'; // vorher: npm install marked
import DOMPurify from 'dompurify';

/**
 * Extract JSON object/array from the first Markdown fenced code block.
 * Supports:
 * - ```json\n{...}\n```
 * - ```json {...}```
 * - ```\n{...}\n```
 * Also works if there is other text before/after the fence.
 */
export function extractJsonFromMarkdownFence(value) {
  if (typeof value !== 'string') return null;

  // Find the first fenced block; be tolerant about whitespace/newlines.
  const match = value.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (!match) return null;

  const jsonCandidate = match[1]?.trim();
  if (!jsonCandidate) return null;

  try {
    return JSON.parse(jsonCandidate);
  } catch {
    return null;
  }
}

// Configure markdown rendering once.
// - `breaks: true` keeps single newlines as <br> (important for structured outputs)
// - Disable header IDs/mangling to avoid unexpected anchors/obfuscation
marked.setOptions({
  gfm: true,
  breaks: true,
  headerIds: false,
  mangle: false,
});

// Markdown-Link `[Titel](https://…)` oder nackte URL, in einem Durchgang.
// Ein kombiniertes Pattern, damit die URL eines gerade erzeugten <a href="…">
// nicht im zweiten Durchgang nochmal als nackte URL erwischt wird.
const LINK_IN_TEXT = /\[([^\]\n]{1,300})\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"']+)/g;

// Satzzeichen am URL-Ende gehoeren zum Satz, nicht zum Link.
const trimUrlPunctuation = (url) => url.replace(/[.,;:!?)\]]+$/, '');

const buildAnchor = (url, label) => {
  // Kein erneutes Escapen von &: der Text stammt bereits aus HTML und
  // wuerde sonst zu &amp;amp; werden. " und ' koennen im href nicht bleiben.
  const href = url.replace(/"/g, '%22').replace(/'/g, '%27');
  return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
};

const linkifyTextSegment = (text) => {
  if (!/https?:\/\//i.test(text)) return text;

  return text.replace(LINK_IN_TEXT, (match, mdLabel, mdUrl, bareUrl) => {
    if (mdUrl) {
      const url = trimUrlPunctuation(mdUrl);
      return buildAnchor(url, mdLabel.trim()) + mdUrl.slice(url.length);
    }
    const url = trimUrlPunctuation(bareUrl);
    if (!url) return match;
    return buildAnchor(url, url) + bareUrl.slice(url.length);
  });
};

/**
 * Wandelt Markdown-Links und nackte URLs im Text in klickbare <a>-Tags um.
 *
 * Noetig, weil der Agent `answer` als HTML liefert (siehe agent_instructions.py) und
 * dieser Zweig nie durch `marked` laeuft – ein vom Modell inline geschriebenes
 * `[titel](https://…)` bliebe sonst als roher Text stehen.
 * Tags selbst und der Inhalt bestehender <a>…</a> bleiben unangetastet.
 *
 * @param {string} html - HTML-Fragment.
 * @returns {string} - HTML mit verlinkten URLs.
 */
export const linkifyHtml = (html) => {
  if (typeof html !== 'string' || !html) return html;
  if (!/https?:\/\//i.test(html)) return html;

  let insideAnchor = false;

  return html
    .split(/(<[^>]*>)/g)
    .map((part) => {
      if (part.startsWith('<') && part.endsWith('>')) {
        const tag = part.toLowerCase();
        if (/^<a[\s>]/.test(tag)) insideAnchor = true;
        else if (tag.startsWith('</a')) insideAnchor = false;
        return part;
      }
      if (insideAnchor || !part) return part;
      return linkifyTextSegment(part);
    })
    .join('');
};

/**
 * Setzt target/rel auf Links, die ohne diese Attribute erzeugt wurden
 * (z. B. von `marked`), damit Quellen in einem neuen Tab oeffnen.
 */
const openLinksInNewTab = (html) =>
  html.replace(/<a\s+(?![^>]*\btarget=)([^>]*href=["']https?:)/gi, '<a target="_blank" rel="noopener noreferrer" $1');

/**
 * Formatiert eine Nachricht generisch basierend auf Markdown-ähnlichen Syntaxen.
 * - **Text** wird fett und mit 14px dargestellt.
 * - Zeilenumbrüche werden in Absätze umgewandelt.
 * - Aufzählungspunkte mit '-' werden als Listen dargestellt.
 * @param {string} message - Die zu formatierende Nachricht.
 * @returns {string} - Die formatierte Nachricht als HTML.
 */
export const formatGenericMessage = (message) => {
  if (typeof message !== 'string') return '';

  // Some backends return structured payloads inside fenced code blocks, e.g.
  // ```json {"question":...,"answer":"..."} ```.
  // In chat we want to show the human-readable `answer`, not the raw JSON wrapper.
  const fenced = extractJsonFromMarkdownFence(message);
  if (fenced && typeof fenced === 'object' && typeof fenced.answer === 'string') {
    message = fenced.answer;
  }

  const trimmed = message.trim();

  // If it's already HTML, keep it but sanitize it (we render via dangerouslySetInnerHTML).
  // `linkifyHtml` davor, weil dieser Zweig `marked` ueberspringt und Markdown-Links
  // sowie nackte URLs sonst als roher Text stehen blieben.
  if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
    return DOMPurify.sanitize(linkifyHtml(message), {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'b', 'i',
        'ul', 'ol', 'li',
        'h1', 'h2', 'h3',
        'blockquote',
        'code', 'pre',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'a',
        'span', 'div'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    });
  }

  // Normalize common formats produced by assistants into Markdown that `marked` can render well.
  // - Convert "• " bullets (also mid-line) into proper list items.
  // - Ensure section headings like "**1. ...**" start on their own block (but NOT numbered lists).
  let normalized = message
    .replace(/\r\n/g, '\n')
    // Add a blank line before bold section headings that typically start a new block.
    // BUT: Only for non-numeric headings to avoid breaking numbered lists (1., 2., 3.)
    .replace(/\n(\*\*(?:Juristische Gesamtwürdigung|Klärungsbedarf|Im Übrigen))/g, '\n\n$1')
    // Convert bullets at line start into markdown list items.
    // Examples:
    //  • Foo   -> - Foo
    //   • Foo  -> - Foo
    .replace(/(^|\n)\s*•\s+/g, '$1- ')
    // Convert sub-bullets that often come as an en-dash into markdown list items.
    // Examples:
    //   – Foo  ->   - Foo
    .replace(/(^|\n)(\s*)–\s+/g, '$1$2- ')
    // Ensure numbered lists (1. 2. 3.) are NOT separated by blank lines
    // Remove extra blank lines between consecutive numbered items
    .replace(/\n\n+(\d+\.\s)/g, '\n$1');

  // Check if this looks like a plain text message (no markdown formatting)
  // If so, convert single newlines to <br> to preserve line breaks
  const hasMarkdownLists = /^[\s]*[-*+]\s|^\d+\.\s/m.test(normalized);
  const hasMarkdownHeaders = /^#{1,6}\s/m.test(normalized);
  const hasMarkdownBoldItalic = /\*{1,2}[^*\n]+\*{1,2}|_{1,2}[^_\n]+_{1,2}/.test(normalized);
  const hasMarkdownFormatting = hasMarkdownLists || hasMarkdownHeaders || hasMarkdownBoldItalic;

  if (!hasMarkdownFormatting) {
    // Plain text: convert newlines to <br> tags to preserve formatting
    normalized = normalized
      .replace(/\n\n+/g, '</p><p>')  // Double newlines become paragraph breaks
      .replace(/\n/g, '<br>');        // Single newlines become line breaks
    normalized = '<p>' + normalized + '</p>';
    return DOMPurify.sanitize(linkifyHtml(normalized), {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  }

  // Parse markdown to HTML, then sanitize.
  let html = marked.parse(normalized);

  // Fix for multiple <ol> lists that should be one continuous list.
  // When text appears between numbered items, marked creates separate <ol> blocks.
  // This causes "1, 1, 1, 1" numbering instead of "1, 2, 3, 4".
  // Solution: Add 'start' attribute to continue numbering from previous list.
  const olRegex = /<ol[^>]*>([\s\S]*?)<\/ol>/g;
  let listItemCount = 0;
  html = html.replace(olRegex, (match, content) => {
    // Count <li> elements in this block
    const liCount = (content.match(/<li>/g) || []).length;

    let result;
    if (listItemCount === 0) {
      result = match; // First list stays unchanged
    } else {
      // Replace <ol> with <ol start="...">
      result = match.replace('<ol>', `<ol start="${listItemCount + 1}">`);
    }

    listItemCount += liCount;
    return result;
  });

  // `marked` erzeugt Links ohne target – Quellen sollen in einem neuen Tab oeffnen.
  html = openLinksInNewTab(html);

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'b', 'i',
      'ul', 'ol', 'li',
      'h1', 'h2', 'h3',
      'blockquote',
      'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'a'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'start'],
  });
};

/**
 * Formatiert einen Zeitstempel sicher und robust.
 * @param {string | number | Date} timestamp - ISO-String, Zahl oder Date-Objekt
 * @param {number} totalTokens - Optional: Token-Anzahl für Bot-Nachrichten
 * @returns {string} - Zeit in 'HH:MM:SS' oder mit Token-Info 'Total Tokens: XXX | HH:MM:SS'
 */
export const formatTimestamp = (timestamp, totalTokens = null) => {
  try {
    if (!timestamp) return '–';

    let date;
    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      return 'Ungültiges Datum';
    }

    if (isNaN(date.getTime())) {
      return 'Ungültiges Datum';
    }

    const timeString = date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    // Füge Token-Informationen hinzu, nur wenn verfügbar und gültig
    if (totalTokens && typeof totalTokens === 'number' && totalTokens > 0) {
      return `Total Tokens: ${totalTokens} | ${timeString}`;
    }

    return timeString;
  } catch (error) {
    console.error('Fehler beim Formatieren des Timestamps:', error);
    return 'Fehler';
  }
};

// Einfache Funktion für lokale Browserzeit
const createLocalTimestamp = () => {
  return new Date().toISOString();
};

/**
 * Initialisiert die Session:
 * - Lädt die Historie
 * - Setzt Fallbacks für timestamp und id
 * - Sendet Start- oder Resume-Nachricht (nur wenn zurückkehrend)
 * @param {Object} session - Die Session
 * @param {Function} setMessages - State Setter für Messages
 * @param {string} app - Die App ID
 * @param {boolean} isReturning - Ob der User zur Session zurückkehrt (nicht erstes Laden)
 */
export const initializeSession = async (session, setMessages, app, isReturning = false) => {
  if (!session || !session.history) {
    // console.log('⚠️ initializeSession: No session or history', session);
    return;
  }

  console.log('📥 initializeSession: Starting for', session._id, 'history length:', session.history?.length);

  try {
    // Verwende die Session-Daten die wir bereits haben, statt neu zu laden
    // (verhindert Timeouts und ist schneller)
    const history = session.history || [];
    const sessionTitle = session.display;

    // console.log('📊 initializeSession: Using existing session data, history length:', history.length);

    if (history.length === 0) {
      // Start-Nachricht mit lokaler Browserzeit
      const { data: startMsg } = await api.post(`/chat/${app}/start`, { message: 'hi' });
      if (startMsg?.output) {
        // Bot-Nachricht mit lokaler Zeit speichern
        await api.post(`/database/sessions/${session._id}/message`, {
          message: {
            user: 'Bot',
            text: startMsg.output,
            type: 'welcome',
            timestamp: createLocalTimestamp() // Lokale Browserzeit
          },
          display: sessionTitle,
          total_tokens: startMsg.total_tokens || null,
          citations: startMsg.citations || null,
          is_json_response: isValidJSON(startMsg.output),
          json_data: isValidJSON(startMsg.output) ? JSON.parse(startMsg.output) : null
        });

        // Historie neu laden
        const { data: updated } = await api.get(`/database/sessions/${session._id}`);
        const updatedHist = (updated.history || []).map((msg) => ({
          ...msg,
          text: formatGenericMessage(msg.text),
          timestamp: msg.timestamp || createLocalTimestamp(), // DB-Zeit direkt verwenden
          id: msg.id || Date.now() + Math.random(),
          totalTokens: msg.total_tokens,
          citations: msg.citations,
          isJsonResponse: msg.is_json_response,
          jsonData: msg.json_data
        }));
        setMessages(updatedHist);
      }
    } else if (history.length > 0) {
      // Historie laden - DB-Zeit direkt verwenden
      const historyWithFormats = await Promise.all(history.map(async (msg) => {
        // DEBUG: Log jede Nachricht aus der DB
        console.log('📨 Message from DB:', {
          text_preview: (msg.text || msg.content || '').substring(0, 50),
          is_json_response: msg.is_json_response,
          has_json_data: !!msg.json_data,
          json_data_type: typeof msg.json_data,
          user: msg.user,
          role: msg.role,
          type: msg.type
        });

        // Unterstütze beide Formate: user: "Bot" und role: "bot"/"assistant"
        const isBot = (msg.user && msg.user.toLowerCase() === 'bot') ||
                      (msg.role && (msg.role.toLowerCase() === 'bot' || msg.role.toLowerCase() === 'assistant'));

        const rawText = msg.text || msg.content || '';

        // Success-Messages, Error-Messages und JSON-Responses nicht formatieren
        const isSuccessMessage = msg.type === 'success';
        const isErrorMessage = msg.type === 'error';
        const isJsonMsg = msg.is_json_response === true;
        // JSON-Responses NICHT mit formatGenericMessage formatieren (würde JSON als Markdown interpretieren)
        const formattedText = (isBot && !isSuccessMessage && !isErrorMessage && !isJsonMsg) ? formatGenericMessage(rawText) : rawText;

        // Generiere SAS URL für Audio-Nachrichten
        let audioUrl = msg.audioUrl || null;
        if (msg.type === 'audio' && msg.blobPath && !audioUrl) {
          try {
            const sasResponse = await api.post('/chat/get-sas-url', { blobPath: msg.blobPath });
            audioUrl = sasResponse.data.url;
            console.log('✅ SAS URL für Audio-Historie generiert:', msg.blobPath);
          } catch (error) {
            console.error('❌ Fehler beim Generieren der SAS URL für Historie:', error);
          }
        }

        // Parse json_data falls es als String vorliegt ODER aus text extrahieren (Fallback für alte Nachrichten)
        let jsonData = msg.json_data;
        let isJsonResponse = msg.is_json_response;

        if (msg.is_json_response && typeof msg.json_data === 'string') {
          try {
            jsonData = JSON.parse(msg.json_data);
            console.log('✅ Parsed json_data from string for message:', msg.text?.substring(0, 50));
          } catch (e) {
            console.error('❌ Failed to parse json_data from history:', e);
            jsonData = null;
          }
        } else if (msg.is_json_response && msg.json_data) {
          console.log('✅ json_data already an object for message:', msg.text?.substring(0, 50));
        } else if (!msg.is_json_response && rawText && rawText.trim().startsWith('{') && rawText.includes('"Report Details"')) {
          // FALLBACK: Alte Nachrichten ohne is_json_response Flag - erkenne JSON im text-Feld
          try {
            jsonData = JSON.parse(rawText);
            isJsonResponse = true;
            console.log('🔄 Fallback: Detected JSON in text field (old message):', rawText.substring(0, 50));
          } catch (e) {
            console.log('⚠️ Text looks like JSON but parse failed:', e);
          }
        }

        const processedMsg = {
          ...msg,
          user: msg.user || (msg.role === 'assistant' || msg.role === 'bot' ? 'Bot' : 'User'), // Normalisiere auf user-Feld
          text: formattedText,
          type: msg.type || 'msg',  // Explizit type beibehalten aus DB
          timestamp: msg.timestamp || msg.updatetime || createLocalTimestamp(),
          id: msg.id || Date.now() + Math.random(),
          totalTokens: msg.total_tokens,
          citations: msg.citations,
          isJsonResponse: isJsonResponse,  // Verwende die ggf. per Fallback erkannte Variable
          jsonData: jsonData,
          isCitationResponse: msg.citations && Array.isArray(msg.citations) && msg.citations.length > 0,
          audioUrl: audioUrl // SAS URL für Audio-Wiedergabe
        };

        // Debug-Log für JSON-Nachrichten
        if (isJsonResponse) {
          console.log('📋 JSON Message Debug:', {
            hasJsonData: !!processedMsg.jsonData,
            isJsonResponse: processedMsg.isJsonResponse,
            type: processedMsg.type,
            jsonDataType: typeof processedMsg.jsonData,
            jsonDataKeys: processedMsg.jsonData ? Object.keys(processedMsg.jsonData) : null
          });
        }

        return processedMsg;
      }));
      console.log('💬 initializeSession: Setting messages, count:', historyWithFormats.length);

      // Debug: Log alle Success-Messages
      const successMessages = historyWithFormats.filter(m => m.type === 'success');
      if (successMessages.length > 0) {
        console.log('✅ Found', successMessages.length, 'success message(s) in history:', successMessages);
      }

      setMessages(historyWithFormats);

      const last = history[history.length - 1];
      // Resume-Nachricht nur senden wenn:
      // 1. Der User zur Session zurückkehrt (isReturning = true)
      // 2. Die letzte Nachricht keine welcome/resume/success Message war
      // 3. Die letzte Nachricht ist vom User (nicht vom Bot)
      // 4. Keine Dokumentenverarbeitung läuft (processing_document)
      const isLastMessageResumeType = ['welcome', 'resume', 'success'].includes(last?.type);
      const isLastMessageFromBot = last?.user?.toLowerCase() === 'bot' || last?.role?.toLowerCase() === 'bot';
      const isProcessingDocument = session.processing_document || false;

      if (isReturning && !isLastMessageResumeType && !isLastMessageFromBot && !isProcessingDocument) {
        // Resume-Nachricht mit lokaler Zeit
        const { data: resumeMsg } = await api.post(`/chat/${app}/resume`, { message: 'hi' });
        if (resumeMsg?.output) {
          const newMsg = {
            user: 'Bot',
            text: formatGenericMessage(resumeMsg.output),
            timestamp: createLocalTimestamp(), // Lokale Zeit
            id: Date.now(),
            totalTokens: resumeMsg.total_tokens,
            citations: resumeMsg.citations,
            isJsonResponse: isValidJSON(resumeMsg.output),
            jsonData: isValidJSON(resumeMsg.output) ? JSON.parse(resumeMsg.output) : null,
            isCitationResponse: resumeMsg.citations && Array.isArray(resumeMsg.citations) && resumeMsg.citations.length > 0
          };
          setMessages((prev) => [...prev, newMsg]);

          // Speichern mit lokaler Zeit
          await api.post(`/database/sessions/${session._id}/message`, {
            message: {
              user: 'Bot',
              text: resumeMsg.output,
              type: 'resume',
              timestamp: createLocalTimestamp() // Lokale Zeit
            },
            display: sessionTitle,
            total_tokens: resumeMsg.total_tokens || null,
            citations: resumeMsg.citations || null,
            is_json_response: isValidJSON(resumeMsg.output),
            json_data: isValidJSON(resumeMsg.output) ? JSON.parse(resumeMsg.output) : null
          });
        }
      }
    }
  } catch (error) {
    // Ignoriere abgebrochene Requests (normal beim Session-Wechsel)
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      console.log('Session initialization was cancelled (session change)');
      return;
    }

    // Bei anderen Fehlern: Log und weitermachen (Backend läuft weiter)
    console.error('Error initializing session:', error);

    // Zeige zumindest die existierende Historie falls vorhanden
    if (session?.history && session.history.length > 0) {
      const historyWithFormats = session.history.map((msg) => {
        const isBot = (msg.user && msg.user.toLowerCase() === 'bot') ||
                      (msg.role && (msg.role.toLowerCase() === 'bot' || msg.role.toLowerCase() === 'assistant'));
        const rawText = msg.text || msg.content || '';

        // Success-Messages, Error-Messages und JSON-Responses nicht formatieren
        const isSuccessMessage = msg.type === 'success';
        const isErrorMessage = msg.type === 'error';
        const isJsonMsg = msg.is_json_response === true;
        // JSON-Responses NICHT mit formatGenericMessage formatieren (würde JSON als Markdown interpretieren)
        const formattedText = (isBot && !isSuccessMessage && !isErrorMessage && !isJsonMsg) ? formatGenericMessage(rawText) : rawText;

        // Parse json_data falls es als String vorliegt
        let jsonData = msg.json_data;
        if (msg.is_json_response && typeof msg.json_data === 'string') {
          try {
            jsonData = JSON.parse(msg.json_data);
          } catch (e) {
            console.error('Failed to parse json_data from history:', e);
            jsonData = null;
          }
        }

        return {
          ...msg,
          user: msg.user || (msg.role === 'assistant' || msg.role === 'bot' ? 'Bot' : 'User'),
          text: formattedText,
          type: msg.type || 'msg',  // Explizit type beibehalten aus DB
          timestamp: msg.timestamp || msg.updatetime || new Date().toISOString(),
          id: msg.id || Date.now() + Math.random(),
          totalTokens: msg.total_tokens,
          citations: msg.citations,
          isJsonResponse: msg.is_json_response,
          jsonData: jsonData,
          isCitationResponse: msg.citations && Array.isArray(msg.citations) && msg.citations.length > 0
        };
      });
      setMessages(historyWithFormats);
    }
  }
};

export const isValidJSON = (str) => {
  if (typeof str !== 'string') return false;

  // Zuerst: Versuche direkt zu parsen
  try {
    JSON.parse(str);
    return true;
  } catch {
    // Falls direktes Parsen fehlschlägt, versuche JSON aus Markdown-Block zu extrahieren
    const extracted = extractJsonFromMarkdownFence(str);
    return extracted !== null && typeof extracted === 'object';
  }
};

export const getInitial = (user) => user.charAt(0).toUpperCase();

/**
 * Formatierung für komplexe JSON-Antworten
 */
export const formatResponseMessageGenerically = (data) => {
  // Early return für ungültige Daten
  if (typeof data === 'string') return formatGenericMessage(data);
  if (!data || typeof data !== 'object') return 'Invalid data format';

  // Wrap in try-catch um White-Screen bei unerwarteten Datenformaten zu verhindern
  try {
    let out = '';
    Object.entries(data).forEach(([key, section]) => {
      out += `<strong style="display:block;margin:10px 0 5px;font-size:14px;">${key}</strong>`;

      if (Array.isArray(section)) {
        // Filtere null/undefined Items heraus
        const validItems = section.filter(item => item != null);

        if (validItems.length === 0) {
          out += '<div>unknown</div>';
        } else {
          const allUnknown = validItems.every((item) => {
            if (typeof item !== 'object') return !item;
            return Object.values(item).every((v) => !v || v === 'unknown');
          });

          if (allUnknown) {
            out += '<div>unknown</div>';
          } else if (key === 'Report Details') {
            validItems.forEach((item) => {
              if (typeof item === 'object' && item !== null) {
                Object.entries(item).forEach(([k, v]) =>
                  out += `<div style="margin-left:5px;"><strong>${k}:</strong> ${v || 'unknown'}</div>`
                );
              }
            });
          } else if (key === 'Teilnehmer') {
            validItems.forEach((item) => {
              if (typeof item === 'object' && item !== null) {
                const personen = Array.isArray(item.personen) ? item.personen.join(', ') : (item.personen || '');
                out += `<div style="margin-left:5px;margin-top:3px;"><strong>${item.gruppe || ''}:</strong> ${personen}</div>`;
              }
            });
          } else if (key === 'Themen') {
            validItems.forEach((item) => {
              if (typeof item === 'object' && item !== null) {
                out += `<div style="margin-left:5px;margin-top:6px;"><strong>${item.thema || ''}</strong>`;
                if (Array.isArray(item.inhalt) && item.inhalt.length > 0) {
                  out += '<ul style="list-style-type:disc;margin-left:15px;">';
                  item.inhalt.forEach((p) => { out += `<li>${(p && p.stichpunkt) || ''}</li>`; });
                  out += '</ul>';
                }
                if (Array.isArray(item.offene_punkte) && item.offene_punkte.length > 0) {
                  out += '<div style="margin-left:10px;"><em>Offene Punkte:</em><ul style="list-style-type:circle;margin-left:15px;">';
                  item.offene_punkte.forEach((p) => { out += `<li>${(p && p.stichpunkt) || ''}</li>`; });
                  out += '</ul></div>';
                }
                out += '</div>';
              }
            });
          } else if (key === 'Besprechungspunkte') {
            out += '<ul style="list-style-type:disc;margin-left:10px;">';
            validItems.forEach((item) => {
              const text = typeof item === 'string'
                ? item
                : (item.besprechungspunkt || Object.values(item)[0] || '');
              const colonIdx = text.indexOf(':');
              if (colonIdx !== -1) {
                const prefix = text.substring(0, colonIdx + 1);
                const rest = text.substring(colonIdx + 1);
                out += `<li><strong>${prefix}</strong>${rest}</li>`;
              } else {
                out += `<li>${text}</li>`;
              }
            });
            out += '</ul>';
          } else {
            out += '<ul style="list-style-type:disc;margin-left:10px;">';
            validItems.forEach((item) => {
              // Wenn item ein String ist, direkt anzeigen
              if (typeof item === 'string') {
                out += `<li>${item}</li>`;
              }
              // Wenn item ein Objekt ist, alle Values anzeigen
              else if (typeof item === 'object' && item !== null) {
                Object.values(item).forEach((val) =>
                  out += `<li>${Array.isArray(val) ? val.join(', ') : (val || 'unknown')}</li>`
                );
              }
            });
            out += '</ul>';
          }
        }
      } else if (section !== null && typeof section === 'object') {
        out += '<ul style="list-style-type:disc;margin-left:10px;">';
        Object.values(section).forEach((val) => (out += `<li>${val || 'unknown'}</li>`));
        out += '</ul>';
      } else {
        out += `<div style="margin-left:5px;">${section || 'unknown'}</div>`;
      }
      out += '<br>';
    });
    return out;
  } catch (error) {
    console.error('Error formatting JSON response:', error, data);
    return '<div>Fehler beim Anzeigen der Antwort</div>';
  }
};

// Lokale Link-Generierung mit file-Feld
export const generateLocalDocumentLink = (citation, baseUrl = window.location.origin) => {
  // Nutze document oder file-Feld
  const docName = citation.document || citation.file;
  const page = citation.page;
  
  if (!docName) return "no document available";
  
  const encodedDocName = encodeURIComponent(docName);
  const pageParam = page ? `#page=${page}` : '';
  
  return `${baseUrl}/documents/${encodedDocName}${pageParam}`;
};

// Erweiterte Link-Validierung mit file-Feld
export const validateAndRegenerateCitationLinks = async (citations, app, sessionId) => {
  if (!citations || !Array.isArray(citations)) return citations;

  const validatedCitations = await Promise.all(
    citations.map(async (citation) => {
      let currentUrl = citation.url;
      
      // Wenn kein Link vorhanden ist, versuche zu generieren
      if (!currentUrl || currentUrl === "no document available") {
        if (citation.document || citation.file) {
          currentUrl = generateLocalDocumentLink(citation);
        }
      }

      // Link validieren
      let linkValid = false;
      if (currentUrl && currentUrl !== "no document available") {
        try {
          const response = await fetch(currentUrl, { 
            method: 'HEAD',
            timeout: 8000
          });
          linkValid = response.ok && response.status === 200;
        } catch (error) {
          console.warn(`Link validation failed for ${currentUrl}:`, error);
          linkValid = false;
        }
      }

      return { 
        ...citation, 
        url: currentUrl,
        linkValid: linkValid 
      };
    })
  );

  return validatedCitations;
};

export const validateCitationLinks = async (citations) => {
  if (!citations || !Array.isArray(citations)) return citations;

  // Validierung nur basierend auf URL-Verfügbarkeit
  // (CORS-Problem mit Azure Blob Storage verhindert HEAD-Requests)
  const validatedCitations = citations.map((citation) => {
    if (!citation.url || citation.url === "no document available") {
      return { ...citation, linkValid: false };
    }
    // Azure Blob Storage URLs und andere URLs als valide markieren
    return { ...citation, linkValid: true };
  });

  return validatedCitations;
};
